import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Save, AlertCircle, X, Search, ChevronDown, MapPin as LocationIcon } from 'lucide-react';
import { searchAddressesByPostcode, getUniqueAddressesForPostcode, parseAddressForForm, formatAddressForDisplay, isValidUKPostcode, type UKAddress } from '../services/ukAddressService';

export interface RetrofitAddressData {
  buildingName?: string;
  houseNumberName?: string;
  streetAddress: string;
  town: string;
  postCode: string;
}

interface RetrofitAddressInputModalProps {
  isOpen: boolean;
  onAddressSubmit: (addressData: RetrofitAddressData) => void;
  onSkip: () => void;
  title: string;
  description: string;
  allowSkip?: boolean;
  initialAddress?: Partial<RetrofitAddressData>;
}

const RetrofitAddressInputModal: React.FC<RetrofitAddressInputModalProps> = ({
  isOpen,
  onAddressSubmit,
  onSkip,
  title,
  description,
  allowSkip = true,
  initialAddress = {}
}) => {
  const [buildingName, setBuildingName] = useState(initialAddress.buildingName || '');
  const [houseNumberName, setHouseNumberName] = useState(initialAddress.houseNumberName || '');
  const [streetAddress, setStreetAddress] = useState(initialAddress.streetAddress || '');
  const [town, setTown] = useState(initialAddress.town || '');
  const [postCode, setPostCode] = useState(initialAddress.postCode || '');
  const [error, setError] = useState('');
  
  // Postcode lookup states
  const [lookupPostcode, setLookupPostcode] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<UKAddress[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [useManualEntry, setUseManualEntry] = useState(false);

  // Only populate fields when modal opens, not on every initialAddress change
  useEffect(() => {
    if (isOpen) {
      setBuildingName(initialAddress.buildingName || '');
      setHouseNumberName(initialAddress.houseNumberName || '');
      setStreetAddress(initialAddress.streetAddress || '');
      setTown(initialAddress.town || '');
      setPostCode(initialAddress.postCode || '');
    }
  }, [isOpen]); // Only depend on isOpen, not initialAddress

  // Direct postcode search (Calendar-style pattern)
  const handlePostcodeSearch = async (postcode: string) => {
    setLookupPostcode(postcode);
    
    if (postcode.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      setSearchError('');
      return;
    }
    
    try {
      setIsSearching(true);
      setSearchError('');
      const results = await searchAddressesByPostcode(postcode);
      setSearchResults(results);
      setShowResults(results.length > 0);
    } catch (err) {
      console.error('Error searching addresses:', err);
      setSearchError('Failed to search addresses. Please try again.');
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle address selection from dropdown
  const handleAddressSelect = (address: UKAddress) => {
    const parsed = parseAddressForForm(address);
    // Store the base street address (without house number)
    setStreetAddress(parsed.streetAddress);
    setTown(parsed.town);
    setPostCode(parsed.postCode);
    // Reset lookup fields
    setLookupPostcode('');
    setSearchResults([]);
    setShowResults(false);
    setSearchError('');
    // If address has a house number, pre-fill it
    if (address.house_number) {
      setHouseNumberName(address.house_number);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!houseNumberName.trim()) {
      setError('Please enter a house number or name');
      return;
    }
    
    if (!streetAddress.trim()) {
      setError('Please enter a street address');
      return;
    }
    
    if (streetAddress.trim().length < 3) {
      setError('Street address must be at least 3 characters long');
      return;
    }

    if (!town.trim()) {
      setError('Please enter a town or city');
      return;
    }

    if (!postCode.trim()) {
      setError('Please enter a post code');
      return;
    }
    
    // Submit with building name and house number as separate fields
    onAddressSubmit({
      buildingName: buildingName.trim() || undefined,
      houseNumberName: houseNumberName.trim() || undefined,
      streetAddress: streetAddress.trim(),
      town: town.trim(),
      postCode: postCode.trim()
    });
  };

  const handleSkip = () => {
    setStreetAddress('');
    setTown('');
    setPostCode('');
    setError('');
    onSkip();
  };

  const handleInputChange = (value: string, field: 'streetAddress' | 'town' | 'postCode') => {
    if (field === 'streetAddress') setStreetAddress(value);
    if (field === 'town') setTown(value);
    if (field === 'postCode') setPostCode(value);
    
    // Clear error when user starts typing and all required fields have content
    if (error && streetAddress.trim().length >= 3 && town.trim() && postCode.trim()) {
      setError('');
    }
  };

  // Toggle between lookup and manual entry
  const toggleEntryMode = () => {
    setUseManualEntry(!useManualEntry);
    setShowResults(false);
    setSearchError('');
    if (!useManualEntry) {
      // Switching to manual - clear lookup data
      setLookupPostcode('');
      setSearchResults([]);
      setHouseNumberName('');
    } else {
      // Switching to lookup - clear manual data
      setStreetAddress('');
      setTown('');
      setPostCode('');
      setError('');
      setHouseNumberName('');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-gradient-to-br from-slate-900/90 via-slate-800/80 to-slate-900/90 backdrop-blur-md flex items-center justify-center z-[9999] p-2 sm:p-4"
      style={{
        paddingTop: 'max(0.5rem, env(safe-area-inset-top))'
      }}
    >
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md mx-2 sm:mx-4 border border-white/20 max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 p-4 sm:p-6">
            <div className="flex items-center space-x-3 relative z-10">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                <MapPin size={20} className="text-white sm:w-6 sm:h-6" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white mb-1">{title}</h2>
                <p className="text-yellow-100 text-sm">{description}</p>
              </div>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-amber-400/20 backdrop-blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 bg-gradient-to-b from-gray-50/50 to-white">
          
          {/* Entry Mode Toggle */}
          <div className="flex items-center justify-center space-x-2 mb-4">
            <button
              type="button"
              onClick={toggleEntryMode}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                !useManualEntry 
                  ? 'bg-yellow-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Search size={14} className="inline mr-1" />
              Postcode Lookup
            </button>
            <button
              type="button"
              onClick={toggleEntryMode}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                useManualEntry 
                  ? 'bg-yellow-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Manual Entry
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {!useManualEntry && (
              <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 space-y-3 sm:space-y-4">
                {/* Postcode Lookup */}
                <div className="relative">
                  <label htmlFor="lookupPostcode" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    Search by Postcode
                  </label>
                  <div className="relative">
                    <input
                      id="lookupPostcode"
                      type="text"
                      value={lookupPostcode}
                      onChange={(e) => handlePostcodeSearch(e.target.value)}
                      placeholder="e.g., OL8 1LU"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 text-sm sm:text-base bg-white border-gray-200 hover:border-gray-300"
                      autoFocus
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      {isSearching ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500"></div>
                      ) : (
                        <Search size={16} className="text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {showResults && searchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-yellow-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {searchResults.map((address, index) => (
                        <button
                          key={`${address.postcode}-${index}`}
                          type="button"
                          onClick={() => handleAddressSelect(address)}
                          className="w-full px-3 py-2 text-left hover:bg-yellow-50 border-b border-gray-100 last:border-b-0 text-sm text-gray-700 hover:text-gray-900 transition-colors duration-150"
                        >
                          <div className="flex items-center">
                            <LocationIcon size={12} className="text-gray-400 mr-2 flex-shrink-0" />
                            <div>
                              <div className="font-medium">{formatAddressForDisplay(address)}</div>
                              <div className="text-xs text-gray-500">{address.postcode}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {searchError && (
                    <div className="mt-2 flex items-center space-x-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle size={14} className="text-red-500" />
                      <span className="text-xs font-medium text-red-600">{searchError}</span>
                    </div>
                  )}
                  
                  {lookupPostcode && lookupPostcode.trim().length >= 3 && !isSearching && searchResults.length === 0 && !searchError && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <span className="text-xs text-amber-700">No addresses found for this postcode.</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {useManualEntry && (
            <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 space-y-3 sm:space-y-4">
              {/* Building Name - OPTIONAL */}
              <div>
                <label htmlFor="buildingNameManual" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Building / Flat Name & Number <span className="text-gray-400 font-normal ml-1">(Optional)</span>
                </label>
                <input
                  id="buildingNameManual"
                  type="text"
                  value={buildingName}
                  onChange={(e) => setBuildingName(e.target.value)}
                  placeholder="e.g., 'Flat 2 Ferndale Court'"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 text-sm sm:text-base bg-white border-gray-200 hover:border-gray-300"
                />
                <p className="mt-1.5 text-xs text-gray-500">For flats, apartments, or buildings with names</p>
              </div>

              {/* House Number/Name - REQUIRED */}
              <div>
                <label htmlFor="houseNumberNameManual" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  House Number / Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="houseNumberNameManual"
                  type="text"
                  value={houseNumberName}
                  onChange={(e) => setHouseNumberName(e.target.value)}
                  placeholder="e.g., 123 or 'Oak House'"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 text-sm sm:text-base bg-white ${
                    error ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  autoFocus
                />
                <p className="mt-1.5 text-xs text-gray-500">Required: Add your house number or property name</p>
              </div>

              {/* Street Address */}
              <div>
                <label htmlFor="streetAddress" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                  Street Address
                </label>
                <input
                  id="streetAddress"
                  type="text"
                  value={streetAddress}
                  onChange={(e) => handleInputChange(e.target.value, 'streetAddress')}
                  placeholder="e.g., 123 Main Street"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 text-sm sm:text-base bg-white ${
                    error ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  autoFocus
                />
              </div>

              {/* Town */}
              <div>
                <label htmlFor="town" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Town/City
                </label>
                <input
                  id="town"
                  type="text"
                  value={town}
                  onChange={(e) => handleInputChange(e.target.value, 'town')}
                  placeholder="e.g., London"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 text-sm sm:text-base bg-white ${
                    error ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : 'border-gray-200 hover:border-gray-300'
                  }`}
                />
              </div>

              {/* Post Code */}
              <div>
                <label htmlFor="postCode" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Post Code
                </label>
                <input
                  id="postCode"
                  type="text"
                  value={postCode}
                  onChange={(e) => handleInputChange(e.target.value, 'postCode')}
                  placeholder="e.g., SW1A 1AA"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 text-sm sm:text-base bg-white ${
                    error ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : 'border-gray-200 hover:border-gray-300'
                  }`}
                />
              </div>

              {error && (
                <div className="flex items-center space-x-2 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle size={16} className="text-red-500" />
                  <span className="text-sm font-medium text-red-600">{error}</span>
                </div>
              )}
            </div>
            )}
            
            {/* House Number/Name Input for Lookup Mode */}
            {!useManualEntry && (streetAddress || town || postCode) && (
              <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 space-y-3 sm:space-y-4">
                <div>
                  <label htmlFor="buildingName" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    Building / Flat Name & Number (Optional)
                  </label>
                  <input
                    id="buildingName"
                    type="text"
                    value={buildingName}
                    onChange={(e) => setBuildingName(e.target.value)}
                    placeholder="e.g., 'Flat 2 Ferndale Court'"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 text-sm sm:text-base bg-white border-gray-200 hover:border-gray-300"
                  />
                  <p className="mt-1.5 text-xs text-gray-500">For flats, apartments, or buildings with names</p>
                </div>
                
                <div>
                  <label htmlFor="houseNumberName" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                    House Number / Name (Optional)
                  </label>
                  <input
                    id="houseNumberName"
                    type="text"
                    value={houseNumberName}
                    onChange={(e) => setHouseNumberName(e.target.value)}
                    placeholder="e.g., 123 or 'Oak House'"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 text-sm sm:text-base bg-white border-gray-200 hover:border-gray-300"
                  />
                  <p className="mt-1.5 text-xs text-gray-500">Add your house number or property name</p>
                </div>
              </div>
            )}
            
            {/* Auto-filled Address Display for Lookup Mode */}
            {!useManualEntry && (streetAddress || town || postCode) && (
              <div className="bg-green-50 rounded-xl border border-green-200 p-3 sm:p-4">
                <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Complete Address Preview
                </h4>
                <div className="text-sm text-green-800 space-y-1 font-medium">
                  {buildingName && <div>{buildingName}</div>}
                  {streetAddress && (
                    <div>
                      {houseNumberName && `${houseNumberName} `}{streetAddress}
                    </div>
                  )}
                  {town && <div>{town}</div>}
                  {postCode && <div>{postCode}</div>}
                </div>
              </div>
            )}

            {/* Benefits info */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 sm:p-4">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Save size={16} className="text-green-600 sm:w-[18px] sm:h-[18px]" />
                </div>
                <div>
                  <h4 className="text-sm sm:text-base font-semibold text-green-800 mb-2">Auto-save Benefits</h4>
                  <ul className="text-xs sm:text-sm text-green-700 space-y-1.5 sm:space-y-2">
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></div>
                      <span>Your work will be automatically saved to the cloud</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></div>
                      <span>Never lose progress due to unexpected closures</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></div>
                      <span>Files organized by property address</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></div>
                      <span>Access your work from any device</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                type="submit"
                disabled={(!useManualEntry && (!streetAddress || !town || !postCode)) || (useManualEntry && (!streetAddress.trim() || !town.trim() || !postCode.trim()))}
                className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-yellow-500 to-amber-500 rounded-xl hover:from-yellow-600 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                Start with Auto-save
              </button>
              {allowSkip ? (
                <button
                  type="button"
                  onClick={handleSkip}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-all duration-200"
                >
                  Skip for now
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSkip}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-all duration-200"
                >
                  Return to Main Menu
                </button>
              )}
            </div>

            {allowSkip && (
              <div className="border-t border-gray-100 pt-4 sm:pt-6">
                <div className="flex items-start space-x-3 text-amber-700 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-3 sm:p-4">
                  <div className="p-1 bg-amber-100 rounded-lg flex-shrink-0">
                    <AlertCircle size={14} className="text-amber-600 sm:w-4 sm:h-4" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-amber-800">
                      <strong>Note:</strong> If you skip, auto-save will be disabled until you add an address later.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Helpful tip for postcode lookup */}
            {!useManualEntry && (
              <div className="border-t border-gray-100 pt-4 sm:pt-6">
                <div className="flex items-start space-x-3 text-blue-700 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 sm:p-4">
                  <div className="p-1 bg-blue-100 rounded-lg flex-shrink-0">
                    <Search size={14} className="text-blue-600 sm:w-4 sm:h-4" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-blue-800">
                      <strong>Tip:</strong> Type your postcode above to automatically find and fill address details from our UK address database.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default RetrofitAddressInputModal;
